// import { useQuery } from '@tanstack/react-query';
// import { useOspApiClientProvider } from './useOspApiClientProvider';

import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "../config/constant";
import { validateAddress } from "../utils/addressValidation";
import { useOspApiClientProvider } from "./useOspApiClientProvider";

interface SwapTokenParams {
  symbol?: string;
  chainId?: number | string;
  tokenAddress?: string;
  chainType?: "evm" | "solana";
}

interface PropertiesAddress {
  address: string;
  chain_id: string;
  network_id: string;
  network_name: string;
  network_logo: string;
}

export interface PropertiesType
  extends MemoCoinType, PropertiesCoinType, NativeCoinType {}

interface PropertiesCoinType {
  ticker?: string;
  token_symbol?: string;
  addresses?: PropertiesAddress[];
}

interface MemoCoinType {
  base_address?: string;
  token_symbol?: string;
}

interface NativeCoinType {
  tags: string[];
  token_symbol?: string;
  ticker?: string;
}

const SwapSupportEvmChainIds = [
  56, 8453, 80094, 1, 42161, 10, 137, 43114, 324, 59144, 5000, 204, 2020, 130,
  146, 11155111,
];

export const getPropertiesAddress = (properties: PropertiesType) => {
  // const property = properties.find((property) => SwapSupportEvmChainIds.includes(property.chain_id))
  const swapSymbol = properties?.ticker || properties?.token_symbol || "";
  const tags = properties?.tags || [];
  if (!!properties?.addresses) {
    const matchProperty = properties?.addresses?.find((property) => {
      const evmMatched = SwapSupportEvmChainIds.includes(
        parseInt(property.chain_id),
      );
      const solMatched = property?.network_id
        ?.toLocaleLowerCase()
        ?.startsWith("solana");
      return evmMatched || solMatched;
    });
    if (!matchProperty) {
      return undefined;
    }
    return {
      tokenAddress: !!matchProperty.address ? matchProperty.address : null,
      symbol: swapSymbol,
      chainId: matchProperty.network_id
        ?.toLocaleLowerCase()
        ?.startsWith("solana")
        ? "solana"
        : parseInt(matchProperty.chain_id),
      chainType: matchProperty?.network_id
        ?.toLocaleLowerCase()
        ?.startsWith("solana")
        ? "solana"
        : "evm",
    } as SwapTokenParams;
  } else if (!!properties?.base_address) {
    const addressType = validateAddress(properties?.base_address);
    if (!addressType.isEVM && !addressType.isSolana) {
      return undefined;
    }
    return {
      tokenAddress: properties?.base_address,
      symbol: swapSymbol,
      chainId: addressType.isSolana ? "solana" : undefined,
      chainType: addressType.isSolana
        ? "solana"
        : addressType.isEVM
          ? "evm"
          : undefined,
    } as SwapTokenParams;
  } else if (!!tags.includes("Native Coin")) {
    if (swapSymbol?.toLocaleLowerCase() === "sol") {
      return {
        tokenAddress: undefined,
        symbol: swapSymbol,
        chainId: "solana",
        chainType: "solana",
      } as SwapTokenParams;
    } else {
      const wrappedSymbol = {
        BTC: "WBTC",
        ETH: "WETH",
        BNB: "WBNB",
        AVAX: "WAVAX",
      }[swapSymbol?.toLocaleUpperCase()];
      return {
        tokenAddress: undefined,
        symbol: wrappedSymbol,
        chainId: 1,
        chainType: "evm",
      } as SwapTokenParams;
    }
  } else if (!!tags.includes("Meme")) {
    return {
      tokenAddress: undefined,
      symbol: swapSymbol,
      chainId: undefined,
      chainType: "evm",
    } as SwapTokenParams;
  }
  return undefined;
};

export const isFetchEnable = (params?: SwapTokenParams) => {
  // const enable = !!params && !!params.chainId && !!params.symbol;
  const evmEnable =
    !!params?.symbol &&
    (typeof params?.chainId === "number" || params?.chainType === "evm");
  const solEnable =
    !!params?.symbol &&
    typeof params?.chainId === "string" &&
    params?.chainId.toLocaleLowerCase().startsWith("solana");
  const enable = evmEnable || solEnable;
  return {
    evmEnable,
    solEnable,
    enable,
  };
};

export const useSwapToken = (params?: SwapTokenParams) => {
  const { symbol, chainId, tokenAddress } = params || {};
  const { evmEnable, solEnable, enable } = isFetchEnable(params);
  const fetchQuery = enable ? tokenAddress || symbol : undefined;

  const { swapApi } = useOspApiClientProvider();
  const {
    data: evmData,
    isLoading: evmLoading,
    isError: evmError,
  } = useQuery({
    queryKey: [QUERY_KEYS.SWAP_TOKEN_EVM, chainId, fetchQuery],
    queryFn: async () => {
      const evmResult = await swapApi?.searchTokensEVMDirect(
        fetchQuery!,
        chainId ? [chainId! as number] : undefined,
        1,
        100,
      );

      // 检查evmResult是否有精确匹配的token，如果没有则整个设为null
      const filteredEvmResult =
        evmResult &&
        evmResult.tokens.filter((token) => {
          // 如果token是honeypot，诈骗币则不返回
          return (
            token.symbol?.toLocaleLowerCase() === symbol?.toLocaleLowerCase() &&
            !token.isHoneypot
          );
        });

      return filteredEvmResult?.[0];
    },
    enabled: evmEnable,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const {
    data: solData,
    isLoading: solLoading,
    isError: solError,
  } = useQuery({
    queryKey: [QUERY_KEYS.SWAP_TOKEN_SOLANA, chainId, fetchQuery],
    queryFn: async () => {
      const solResult = await swapApi?.searchTokens("solana", fetchQuery!);
      const filteredSolResult =
        solResult &&
        solResult.tokens.filter((token) => {
          // 如果token是honeypot，诈骗币则不返回
          return (
            token.symbol?.toLocaleLowerCase() === symbol?.toLocaleLowerCase() &&
            !token.isHoneypot
          );
        });
      return filteredSolResult?.[0]
        ? { ...filteredSolResult?.[0], chainId: chainId }
        : undefined;
    },
    enabled: solEnable,
  });

  return {
    data: evmData || solData,
    isLoading: evmLoading || solLoading,
    isError: evmError || solError,
    type: evmEnable ? "evm" : solEnable ? "solana" : undefined,
  };
};
